import { useState } from "react";
import { useNavigate } from "react-router";
import { Check, MessageCircleQuestion, X } from "lucide-react";
import {
  VButton,
  VDialog,
  VDialogContent,
  VField,
  VTextarea,
} from "@/components/ui";
import { useCurrentUser } from "@/domains/auth";
import type { CameraRequest } from "@/domains/requests/api/types";
import {
  useApproveRequest,
  useRejectRequest,
  useRequestClarification,
} from "@/domains/requests/api/mutations";

type RequestDecisionActionsProps = {
  request: CameraRequest;
};

type DialogMode = "approve" | "reject" | "clarify" | null;

const DIALOG_LABELS: Record<Exclude<DialogMode, null>, {
  title: string;
  description: string;
  fieldLabel: string;
  placeholder: string;
  confirmLabel: string;
  required: boolean;
}> = {
  approve: {
    title: "Aanvraag goedkeuren",
    description:
      "Bij goedkeuring wordt automatisch een dossier aangemaakt vanuit deze aanvraag.",
    fieldLabel: "Optionele opmerking",
    placeholder: "Bv. 'Plaatsing wordt vandaag nog ingepland.'",
    confirmLabel: "Goedkeuren & dossier aanmaken",
    required: false,
  },
  reject: {
    title: "Aanvraag afwijzen",
    description: "Geef een duidelijke reden, de aanvrager ziet deze in zijn dossier.",
    fieldLabel: "Reden van afwijzing",
    placeholder: "Bv. 'Onvoldoende motivatie in verhouding tot privacy-impact.'",
    confirmLabel: "Afwijzen",
    required: true,
  },
  clarify: {
    title: "Verduidelijking vragen",
    description:
      "De aanvraag blijft openstaan en de aanvrager wordt gevraagd om bijkomende informatie.",
    fieldLabel: "Wat moet er verduidelijkt worden?",
    placeholder: "Bv. 'Graag duidelijker omschrijven welke kijkrichting gewenst is.'",
    confirmLabel: "Vraag verduidelijking",
    required: true,
  },
};

export function RequestDecisionActions({
  request,
}: RequestDecisionActionsProps): React.JSX.Element | null {
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const [mode, setMode] = useState<DialogMode>(null);
  const [message, setMessage] = useState("");

  const approve = useApproveRequest();
  const reject = useRejectRequest();
  const clarify = useRequestClarification();

  if (currentUser.role !== "manager") return null;
  const canDecide =
    request.status === "submitted" ||
    request.status === "inReview" ||
    request.status === "awaitingClarification";
  if (!canDecide) return null;

  const manager = {
    id: currentUser.id,
    name: `${currentUser.firstName} ${currentUser.lastName}`,
  };

  const closeDialog = (): void => {
    setMode(null);
    setMessage("");
  };

  const handleConfirm = (): void => {
    if (mode === "approve") {
      approve.mutate(
        { request, manager, message: message.trim() || undefined },
        {
          onSuccess: ({ case: created }) => {
            closeDialog();
            navigate(`/cases/${created.id}`);
          },
        },
      );
    } else if (mode === "reject") {
      reject.mutate(
        { id: request.id, manager, reason: message.trim() },
        { onSuccess: closeDialog },
      );
    } else if (mode === "clarify") {
      clarify.mutate(
        { id: request.id, manager, question: message.trim() },
        { onSuccess: closeDialog },
      );
    }
  };

  const isPending = approve.isPending || reject.isPending || clarify.isPending;
  const config = mode ? DIALOG_LABELS[mode] : null;
  const trimmed = message.trim();
  const isValid = config ? (config.required ? trimmed.length > 0 : true) : false;

  return (
    <>
      <VButton onClick={() => setMode("approve")}>
        <Check className="size-4" aria-hidden />
        Goedkeuren
      </VButton>
      <VButton variant="outline" onClick={() => setMode("clarify")}>
        <MessageCircleQuestion className="size-4" aria-hidden />
        Vraag verduidelijking
      </VButton>
      <VButton
        variant="outline"
        className="border-error-300 text-error-700 hover:border-error-400 hover:bg-error-50 hover:text-error-800"
        onClick={() => setMode("reject")}
      >
        <X className="size-4" aria-hidden />
        Afwijzen
      </VButton>

      <VDialog
        open={mode !== null}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        {config ? (
          <VDialogContent title={config.title} description={config.description}>
            <div className="space-y-4">
              <VField
                label={config.fieldLabel}
                htmlFor="decisionMessage"
                required={config.required}
              >
                <VTextarea
                  id="decisionMessage"
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={config.placeholder}
                  autoFocus
                />
              </VField>
              <div className="flex justify-end gap-2 pt-2">
                <VButton variant="ghost" onClick={closeDialog} disabled={isPending}>
                  Annuleren
                </VButton>
                <VButton
                  onClick={handleConfirm}
                  disabled={!isValid || isPending}
                  variant={mode === "reject" ? "destructive" : "primary"}
                >
                  {isPending ? "Bezig…" : config.confirmLabel}
                </VButton>
              </div>
            </div>
          </VDialogContent>
        ) : null}
      </VDialog>
    </>
  );
}
